import type {
  Arrangement,
  ImportArrangement,
  RoofHalfEdge,
  RoofVertex,
} from "building-types/roofVolumes/roofVolumeTypes";
import { ensureDefined } from "common/utils/assertOfType";
import type { Face } from "geom/brep";
import { faceNew } from "geom/brep";
import { planeByThreePoints, planeNormal, planeProjectPolyLine3, planeReverse } from "geom/plane";
import { polyLine3New } from "geom/polyLine3";
import { vec3Dot } from "geom/vec3";
import { exportHalfEdge, getPlane } from "./halfEdgeUtils";
import { connectHalfEdges, exportVertex } from "./vertexUtils";

// Add a new vertex to the arrangement. This also add its half-edges and their twins.
export function addVertexToArrangement(arrangement: Arrangement, vertex: RoofVertex) {
  arrangement.vertices.push(vertex);
  const twins = vertex.halfEdges.map((half) => ensureDefined(half.twin));
  arrangement.halfEdges = arrangement.halfEdges.concat(vertex.halfEdges).concat(twins);
}

// Connect two half-edges together and remove their twins.
export function connectHalfs(arrangement: Arrangement, half0: RoofHalfEdge, half1: RoofHalfEdge) {
  removeHalfsFromArrangement(arrangement, [ensureDefined(half0.twin), ensureDefined(half1.twin)]);
  half0.twin = half1;
  half1.twin = half0;

  half0.origin && connectHalfEdges(half0.origin);
  half1.origin && connectHalfEdges(half1.origin);
}

// Connect two half-edges together and connect their twins.
export function connectHalfsAndTwin(half0: RoofHalfEdge, half1: RoofHalfEdge) {
  const twin0 = ensureDefined(half0.twin);
  const twin1 = ensureDefined(half1.twin);
  half0.twin = half1;
  half1.twin = half0;
  twin0.twin = twin1;
  twin1.twin = twin0;
  half0.origin && connectHalfEdges(half0.origin);
  half1.origin && connectHalfEdges(half1.origin);
  twin0.origin && connectHalfEdges(twin0.origin);
  twin1.origin && connectHalfEdges(twin1.origin);
}

export function removeHalfsFromArrangement(arrangement: Arrangement, halfsToRemove: RoofHalfEdge[]) {
  arrangement.halfEdges = arrangement.halfEdges.filter((half) => {
    return !halfsToRemove.includes(half);
  });
}

// Remove a vertex and all vertices connected to it.
export function removeVertexAndNeighbors(arrangement: Arrangement, vertexToRemove: RoofVertex) {
  const verticesToRemove: RoofVertex[] = [vertexToRemove];
  const vertexQueue: RoofVertex[] = [vertexToRemove];
  while (vertexQueue.length > 0) {
    const vertex = vertexQueue.shift();
    vertex?.halfEdges.forEach((half) => {
      const neighbor = half.twin?.origin;
      if (neighbor?.position && !verticesToRemove.includes(neighbor)) {
        verticesToRemove.push(neighbor);
        vertexQueue.push(neighbor);
      }
    });
  }
  const halfEdgesToRemove: RoofHalfEdge[] = [];
  verticesToRemove.forEach((vertex) => {
    halfEdgesToRemove.push(...vertex.halfEdges);
  });
  removeHalfsFromArrangement(arrangement, halfEdgesToRemove);
  arrangement.vertices = arrangement.vertices.filter((vertex) => {
    return !verticesToRemove.includes(vertex);
  });
}

// Export an arrangement into a format that can be imported.
export function exportArrangement(arrangement: Arrangement): ImportArrangement {
  const vertices = arrangement.vertices.slice();
  arrangement.halfEdges.forEach((half) => {
    if (half.origin && !vertices.includes(half.origin)) {
      vertices.push(half.origin);
    }
  });
  return {
    vertices: vertices.map((vertex) => exportVertex(vertex, arrangement.halfEdges)),
    vIndices: arrangement.vertices.map((vertex) => vertices.indexOf(vertex)),
    halfEdges: arrangement.halfEdges.map((half) => exportHalfEdge(half, arrangement.halfEdges, vertices)),
  };
}

// Export the arrangement to a string. This string can be imported into the roof prototype for debugging.
export function exportToString(inputArrangement: Arrangement): string {
  const arrangement = exportArrangement(inputArrangement);
  const vertices = arrangement.vertices.map((v) => {
    const position = v.position ? { x: v.position[0], y: v.position[1], z: v.position[2] } : undefined;
    const dir = v.dir ? { x: v.dir[0], y: v.dir[1], z: v.dir[2] } : undefined;
    return { position, dir, halfEdges: v.halfEdges };
  });

  const converted = { ...arrangement, vertices };
  return JSON.stringify({ arrangement: converted, vertices: [], edges: [] });
}

// Create a face from a list of its half-edges.
export function halfEdgesToFace(halfEdges: RoofHalfEdge[]): Face | null {
  const getPosition = (halfEdge: RoofHalfEdge) => halfEdge.origin?.position;
  const a = getPosition(halfEdges[0]);
  const origin = getPosition(halfEdges[1]);
  const b = getPosition(halfEdges[2]);
  if (!a || !b || !origin) {
    return null;
  }
  const planeActual = planeByThreePoints(origin[0], origin[1], origin[2], a[0], a[1], a[2], b[0], b[1], b[2]);
  const planeExpected = getPlane(halfEdges[0]);
  if (!planeActual || !planeExpected) {
    return null;
  }
  const { nX, nY, nZ } = planeExpected;
  const [px, py, pz] = planeNormal(planeActual);

  // Flip the normal if it is the opposite of what is expected.
  if (vec3Dot(nX, nY, nZ, px, py, pz) < 0) {
    planeReverse(planeActual);
  }

  // Add the half-edge positions to a polyLine.
  const polyLine3: number[] = [];
  const addPosition = (halfEdge: RoofHalfEdge) => {
    const position = getPosition(halfEdge);
    if (position) {
      polyLine3.push(position[0]);
      polyLine3.push(position[1]);
      polyLine3.push(position[2]);
    }
  };
  halfEdges.forEach((halfEdge) => {
    addPosition(halfEdge);
  });
  // Add the first position again to close the face.
  addPosition(halfEdges[0]);

  const loop = planeProjectPolyLine3(planeActual, polyLine3New(polyLine3));
  return faceNew([loop], planeActual);
}

// Get all half-edges, each in a separate array one for each face.
export function getFaceHalfEdges(arrangement: Arrangement): RoofHalfEdge[][] {
  const { halfEdges } = arrangement;
  const allFaceHalfEdges: RoofHalfEdge[][] = [];

  // To find the faces, start at each half-edge and go to the next one until it repeats itself.
  // Keep track of which half-edges is visited so we don't repeat the same face twice.
  const visited: boolean[] = [];
  halfEdges.forEach((_, index) => {
    visited[index] = false;
  });

  halfEdges.forEach((half0: RoofHalfEdge) => {
    const faceHalfEdges: RoofHalfEdge[] = [];
    let halfIndex = halfEdges.indexOf(half0);
    if (visited[halfIndex]) {
      return;
    }
    let currHalf: RoofHalfEdge | undefined = half0;
    while (currHalf && !visited[halfIndex]) {
      visited[halfIndex] = true;
      faceHalfEdges.push(currHalf);
      currHalf = currHalf.next;
      if (currHalf) {
        halfIndex = halfEdges.indexOf(currHalf);
      }
    }
    if (currHalf !== half0) {
      return;
    }
    if (faceHalfEdges.length > 0) {
      allFaceHalfEdges.push(faceHalfEdges);
    }
  });
  return allFaceHalfEdges;
}
